import { useEffect, useMemo, useRef, useState } from "react";
import { useApiFetch } from "../../api/client";
import { Card } from "../../components/ui/Card";
import { Chip } from "../../components/ui/Chip";
import { AnnouncementInsights } from "./AnnouncementInsights";
import { shiftMonth } from "./callForm";
import { errorMessage, monthName, monthOf, shortDateLabel } from "./format";
import {
  ANNOUNCEMENT_STATUS_LABELS,
  buildNominationBoard,
  nominationAmountLabel,
  type NominationCard,
} from "./nominationBoard";
import { callCheckInsight, lastMonthInsight } from "./nominationInsightMath";
import { latestOwnCallFmp } from "./priceContextChart";
import { SectionHeader } from "./SectionHeader";
import { nominationDrawerContent, type SourceDrawerContent } from "./sourceDrawerContent";
import type { Call, CallGuidance, Nomination, RosterSupplier } from "./types";

interface Props {
  month: string;
  resinFamilyCode: string;
  logIntelligenceVersion: number;
  callVersion: number;
  appUserId: number | null;
  onOpenDrawer: (content: SourceDrawerContent) => void;
}

interface LoadedNominationData {
  loadKey: string;
  roster: RosterSupplier[];
  nominations: Nomination[];
  lastMonthNominations: Nomination[];
  callsThisMonth: Call[];
  guidance: CallGuidance;
}

function SupplierCard({ card, onOpen }: { card: NominationCard; onOpen: () => void }) {
  const { announcementStatus, latestNomination } = card;
  const amountCentsPerLb = latestNomination?.amountCentsPerLb;
  return (
    <button
      type="button"
      className={`flex flex-col gap-2 text-left bg-transparent border border-rs-jet rounded-md p-3.5 cursor-pointer hover:border-rs-green ${
        announcementStatus === "NotYetChecked" ? "border-dashed" : ""
      }`}
      onClick={onOpen}
    >
      <b className="text-sm text-rs-off-white">{card.supplierName}</b>
      <span className="flex items-center gap-2">
        {announcementStatus === "Announced" && amountCentsPerLb !== null && amountCentsPerLb !== undefined ? (
          <span className="text-lg font-semibold text-rs-green">{nominationAmountLabel(amountCentsPerLb)}</span>
        ) : (
          <span className="text-lg font-semibold text-rs-light-gray">
            {announcementStatus === "NoAnnouncement" ? "—" : "?"}
          </span>
        )}
        <Chip variant={announcementStatus === "Announced" ? "ok" : "default"}>
          {ANNOUNCEMENT_STATUS_LABELS[announcementStatus]}
        </Chip>
      </span>
      <small className="text-xs text-rs-light-gray">
        {latestNomination
          ? `${latestNomination.sourceReference} · Checked ${shortDateLabel(latestNomination.checkDate)}`
          : "No record this month"}
      </small>
    </button>
  );
}

export function WhoHasAnnounced({
  month,
  resinFamilyCode,
  logIntelligenceVersion,
  callVersion,
  appUserId,
  onOpenDrawer,
}: Props) {
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const loadKey = `${resinFamilyCode}|${month}`;
  const [loadedData, setLoadedData] = useState<LoadedNominationData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let isStale = false;
    const resinQuery = `resinFamily=${encodeURIComponent(resinFamilyCode)}`;
    const thisMonthQuery = `month=${month}-01&${resinQuery}`;
    const lastMonthQuery = `month=${shiftMonth(month, -1)}-01&${resinQuery}`;
    const loadJson = (path: string) => apiFetchRef.current(path).then((response) => response.json());
    Promise.all([
      loadJson("consultant-cockpit/roster?trackedOnly=true"),
      loadJson(`consultant-cockpit/nominations?${thisMonthQuery}`),
      loadJson(`consultant-cockpit/nominations?${lastMonthQuery}`),
      loadJson(`consultant-cockpit/make-the-call?${thisMonthQuery}`),
      loadJson(`consultant-cockpit/make-the-call/guidance?${thisMonthQuery}`),
    ])
      .then(
        ([roster, nominations, lastMonthNominations, callsThisMonth, guidance]: [
          RosterSupplier[],
          Nomination[],
          Nomination[],
          Call[],
          CallGuidance,
        ]) => {
          if (isStale) return;
          setLoadedData({ loadKey, roster, nominations, lastMonthNominations, callsThisMonth, guidance });
          setLoadError(null);
        },
      )
      .catch((error) => {
        if (!isStale) setLoadError(errorMessage(error));
      });
    return () => {
      isStale = true;
    };
  }, [month, resinFamilyCode, loadKey, logIntelligenceVersion, callVersion]);

  const boardWithInsights = useMemo(() => {
    if (!loadedData || loadedData.loadKey !== loadKey) return null;
    const board = buildNominationBoard(loadedData.roster, loadedData.nominations);
    const lastMonthBoard = buildNominationBoard(loadedData.roster, loadedData.lastMonthNominations);
    const priorCall = loadedData.guidance.priorCall;
    const callCheck = callCheckInsight(
      resinFamilyCode,
      month,
      {
        currentFmpUsdPerLb: latestOwnCallFmp(loadedData.callsThisMonth, appUserId),
        priorFmpUsdPerLb: priorCall?.fmpUsdPerLb ?? null,
        priorCallMonth: priorCall ? monthOf(priorCall.callMonth) : null,
      },
      board,
    );
    const lastMonthComparison = lastMonthInsight(month, board, lastMonthBoard);
    return { board, callCheck, lastMonthComparison };
  }, [loadedData, loadKey, resinFamilyCode, month, appUserId]);
  const board = boardWithInsights?.board ?? null;

  return (
    <section className="lane on" aria-labelledby="nominations-heading">
      <SectionHeader sectionId="nominations-section">
        <Chip>{monthName(month)}</Chip>
        <Chip variant="ok">{resinFamilyCode}</Chip>
      </SectionHeader>

      <Card>
        {loadError ? (
          <p className="m-0 text-rs-light-gray text-[13px]">Couldn't load nominations: {loadError}</p>
        ) : !board ? (
          <p className="m-0 text-rs-light-gray text-[13px]">Loading…</p>
        ) : board.cards.length === 0 ? (
          <p className="m-0 text-rs-light-gray text-[13px]">
            No suppliers are tracked yet. Add them on the Supplier Roster tab in Log Intelligence.
          </p>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {board.cards.map((card) => (
                <SupplierCard
                  key={card.supplierId}
                  card={card}
                  onOpen={() => onOpenDrawer(nominationDrawerContent(card, month))}
                />
              ))}
            </div>
            {boardWithInsights && (
              <AnnouncementInsights
                callCheck={boardWithInsights.callCheck}
                lastMonthComparison={boardWithInsights.lastMonthComparison}
              />
            )}
            <div className="flex flex-wrap gap-x-6 gap-y-1 mt-4 pt-3 border-t border-rs-jet text-[13px] text-rs-light-gray">
              <span>
                <strong className="text-rs-off-white">
                  {board.checkedSupplierCount} of {board.cards.length}
                </strong>{" "}
                tracked suppliers checked
              </span>
            </div>
          </>
        )}
      </Card>
    </section>
  );
}
